(() => {
   const setupScrollFloat = () => {
    const sections = Array.from(document.querySelectorAll('[data-scroll-float]'));
    if (!sections.length || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let ticking = false;
    const update = () => {
      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        const viewport = window.innerHeight || document.documentElement.clientHeight;
        if (rect.bottom < 0 || rect.top > viewport) return;

        const progress = (viewport - rect.top) / (viewport + rect.height);
        const clamped = Math.min(Math.max(progress, 0), 1);

        section.querySelectorAll('[data-float-item]').forEach((item) => {
          const speed = Number(item.dataset.floatSpeed || -0.08);
          const offset = (clamped - 0.35) * rect.height * speed;
          item.style.setProperty('--fxbg-float-y', `${offset.toFixed(2)}px`);
        });
      });
      ticking = false;
    };

    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupScrollFloat, { once: true });
  } else {
    setupScrollFloat();
  }

  if (customElements.get('sticker-product-configurator')) return;

  class StickerProductConfigurator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.priceNode = this.querySelector('[data-sticker-price]');
      this.buttonPriceNode = this.querySelector('[data-sticker-button-price]');
      this.unitPriceNode = this.querySelector('[data-sticker-unit-price]');
      this.summaryNode = this.querySelector('[data-sticker-summary]');
      this.breakdownNodes = {
        base: this.querySelector('[data-price-base]'),
        size: this.querySelector('[data-price-size]'),
        finish: this.querySelector('[data-price-finish]'),
        turnaround: this.querySelector('[data-price-turnaround]')
      };
      this.hiddenProperties = {
        size: this.querySelector('[data-sticker-property="size"]'),
        quantity: this.querySelector('[data-sticker-property="quantity"]'),
        finish: this.querySelector('[data-sticker-property="finish"]'),
        turnaround: this.querySelector('[data-sticker-property="turnaround"]'),
        price: this.querySelector('[data-sticker-property="price"]')
      };

      this.addEventListener('change', (event) => {
        if (event.target.matches('input[type="radio"]')) this.updatePrice();
        if (event.target.matches('input[type="file"]')) this.updateUploadLabel(event.target);
      });

      this.setupUploadZone();
      this.updatePrice();
    }

    getSelected(name) {
      return this.querySelector(`input[name="${name}"]:checked`);
    }

    updatePrice() {
      const size = this.getSelected('sticker_size');
      const quantity = this.getSelected('sticker_quantity');
      const finish = this.getSelected('sticker_finish');
      const turnaround = this.getSelected('sticker_turnaround');
      if (!size || !quantity || !finish || !turnaround) return;

      const basePrice = Number(quantity.dataset.basePrice || 0);
      const sizeMultiplier = Number(size.dataset.multiplier || 1);
      const finishAdder = Number(finish.dataset.adder || 0);
      const turnaroundAdder = Number(turnaround.dataset.adder || 0);
      const total = basePrice * sizeMultiplier * (1 + finishAdder) * (1 + turnaroundAdder);
      const rounded = Math.round(total * 100) / 100;
      const formatted = this.formatMoney(rounded);
      const unitPrice = rounded / Number(quantity.value);
      const summary = `${quantity.value} custom ${size.value} ${finish.value.toLowerCase()} stickers, ${turnaround.value.toLowerCase()}`;

      if (this.priceNode) this.priceNode.textContent = formatted;
      if (this.buttonPriceNode) this.buttonPriceNode.textContent = formatted;
      if (this.unitPriceNode) this.unitPriceNode.textContent = `${this.formatMoney(unitPrice)} each`;
      if (this.summaryNode) this.summaryNode.textContent = summary;
      this.updateBreakdown(basePrice, sizeMultiplier, finishAdder, turnaroundAdder);

      this.hiddenProperties.size.value = size.value;
      this.hiddenProperties.quantity.value = quantity.value;
      this.hiddenProperties.finish.value = finish.value;
      this.hiddenProperties.turnaround.value = turnaround.value;
      this.hiddenProperties.price.value = formatted;
    }

    updateBreakdown(basePrice, sizeMultiplier, finishAdder, turnaroundAdder) {
      if (this.breakdownNodes.base) this.breakdownNodes.base.textContent = this.formatMoney(basePrice);
      if (this.breakdownNodes.size) {
        this.breakdownNodes.size.textContent = sizeMultiplier === 1 ? 'Included' : `x${sizeMultiplier.toFixed(2)}`;
      }
      if (this.breakdownNodes.finish) {
        this.breakdownNodes.finish.textContent = finishAdder === 0 ? 'Included' : `+${Math.round(finishAdder * 100)}%`;
      }
      if (this.breakdownNodes.turnaround) {
        this.breakdownNodes.turnaround.textContent = turnaroundAdder === 0 ? 'Included' : `+${Math.round(turnaroundAdder * 100)}%`;
      }
    }

    formatMoney(amount) {
      const currency = this.dataset.currency || 'USD';
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency
        }).format(amount);
      } catch (error) {
        return `$${amount.toFixed(2)}`;
      }
    }

    setupUploadZone() {
      const zone = this.querySelector('[data-upload-zone]');
      if (!zone) return;

      zone.addEventListener('drop', (event) => {
        event.preventDefault();
        const input = zone.querySelector('input[type="file"]');
        if (!input || !event.dataTransfer?.files.length) return;
        const transfer = new DataTransfer();
        transfer.items.add(event.dataTransfer.files[0]);
        input.files = transfer.files;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      ['dragenter', 'dragover'].forEach((eventName) => {
        zone.addEventListener(eventName, (event) => {
          event.preventDefault();
          zone.classList.add('is-dragging');
        });
      });

      ['dragleave', 'drop'].forEach((eventName) => {
        zone.addEventListener(eventName, () => zone.classList.remove('is-dragging'));
      });
    }

    updateUploadLabel(input) {
      const copy = this.querySelector('[data-upload-copy]');
      const file = input.files?.[0];
      if (copy) copy.textContent = file ? file.name : 'Drop a file here or tap to browse';
      this.updateArtworkPreview(file);
    }

    disconnectedCallback() {
      this.contourEditor?.destroy();
      this.contourEditor = null;
      this.clearArtworkPreview();
    }

    clearArtworkPreview() {
      if (this.artworkImage) {
        this.artworkImage.onload = null;
        this.artworkImage.onerror = null;
        this.artworkImage.remove();
        this.artworkImage = null;
      }
      if (this.artworkUrl) {
        URL.revokeObjectURL(this.artworkUrl);
        this.artworkUrl = null;
      }
      const original = this.querySelector('.fxbg-sticker__preview > .fxbg-sticker__image');
      if (original) original.hidden = false;
    }

    updateArtworkPreview(file) {
      this.clearArtworkPreview();
      if (window.FXBGContourEditor) {
        this.contourEditor ||= new window.FXBGContourEditor(this);
        this.contourEditor.setFile(file);
        return;
      }
      const preview = this.querySelector('.fxbg-sticker__preview');
      if (!preview) return;
      let status = this.querySelector('[data-artwork-preview-status]');
      if (!status) {
        status = document.createElement('p');
        status.dataset.artworkPreviewStatus = '';
        status.setAttribute('role', 'status');
        preview.insertAdjacentElement('afterend', status);
      }
      status.textContent = '';
      if (!file) return;
      if (!/\.(png|jpe?g|webp)$/i.test(file.name)) {
        status.textContent = 'Artwork selected. Preview is available for PNG, JPG, and WebP files. Other files will be reviewed during proofing.';
        return;
      }
      const image = document.createElement('img');
      image.className = 'fxbg-sticker__image';
      image.alt = `Artwork preview: ${file.name}`;
      image.hidden = true;
      this.artworkImage = image;
      this.artworkUrl = URL.createObjectURL(file);
      status.textContent = 'Loading artwork preview...';
      image.onload = () => {
        const original = preview.querySelector('.fxbg-sticker__image:not([data-uploaded-artwork])');
        if (original) original.hidden = true;
        image.hidden = false;
        status.textContent = 'Artwork preview only. Your final cut line and layout will be confirmed in your proof.';
      };
      image.onerror = () => {
        this.clearArtworkPreview();
        status.textContent = 'This image could not be previewed. Try a PNG, JPG, or WebP export.';
      };
      image.dataset.uploadedArtwork = '';
      preview.appendChild(image);
      image.src = this.artworkUrl;
    }
  }

  customElements.define('sticker-product-configurator', StickerProductConfigurator);
})();
